/**
 * ============================================================================
 * ⭐⭐ B5 — SEARCH THE LOADED ORDERS FIRST (2026-09-28)
 * ============================================================================
 *
 * > Pete, 2026-09-27: *"Search should search the loaded items first, then out to
 * > database"* (open item 53).
 *
 * Typing in the Orders panel's box narrows the rows ALREADY LOADED, on every
 * keystroke, with no request. Enter (or the magnifier) is still the explicit
 * "go ask the database, every date".
 *
 * ⛔ THE TRAP IS THE CAP, AND THE PANEL SAYS SO. The loaded list is capped and
 * dated; an order can exist and not be in it. So a local miss is never phrased as
 * "not found" — it is "none of the N loaded orders", with Enter offered in place.
 *   ** A FILTER OVER A SAMPLE MUST NAME THE SAMPLE. **
 *
 * ⭐ A PURE FUNCTION so a guard can run it, and so the fields searched are one
 * list, not a condition spread through JSX.
 */

export interface LocalSearchRow {
    orderNumber: string;
    customerOrderNumber: string | null;
    customerReference: string | null;
    containerNumber: string | null;
    bolNumber: string | null;
    proNumber: string | null;
    customer: string | null;
    businessUnit: string | null;
    bookedOn?: readonly { appointmentNumber: string | null }[] | null;
}

/** The text a loaded row can be found by — every identifier the card can show. */
function fieldsOf(r: LocalSearchRow): string[] {
    return [
        r.orderNumber, r.customerOrderNumber, r.customerReference, r.containerNumber,
        r.bolNumber, r.proNumber, r.customer, r.businessUnit,
        ...(r.bookedOn ?? []).map(b => b.appointmentNumber),
    ].filter((v): v is string => typeof v === 'string' && v.trim() !== '');
}

/**
 * Does this loaded row match what is typed? Case-insensitive, substring, and
 * separator-blind (`MSCU 123-4` finds `MSCU1234`) — the way numbers are read out
 * at a gate. ⚠️ Blank matches everything: an empty box filters nothing.
 */
export function loadedOrderMatches(r: LocalSearchRow, typed: string): boolean {
    const norm = (s: string) => s.toLowerCase().replace(/[\s\-_./]+/g, '');
    const needle = norm(typed);
    if (!needle) return true;
    return fieldsOf(r).some(f => norm(f).includes(needle));
}
